// Dependency-free, so `node --test` can load it.

/**
 * A payment that left this tab for its provider (PayPal, Amazon Pay, Wero, a bank's 3-D Secure page) and has not come
 * back through the return URL (CIN-15446). PayPal's app switch on a phone leaves the original tab on the checkout
 * with no answer: the PaymentIntent tells this tab what happened instead.
 */
export type PendingRedirect = {
    clientSecret: string;
    method: string | null;
    /** The cart it pays: a later booking in this tab must not take an old payment for its own. */
    cartUuid: string;
};

/** What the tab does with the PaymentIntent's status: go on to the booking, offer to finish or switch, or wait. */
export type RedirectRecheck = 'complete' | 'stalled' | 'canceled' | 'wait';

export function redirectRecheck(status: string | null | undefined): RedirectRecheck {
    switch (status) {
        // Authorized (manual capture), being processed by the bank, or captured: the webhook takes it from here.
        case 'requires_capture':
        case 'processing':
        case 'succeeded':
            return 'complete';
        // The buyer has not confirmed at the provider yet.
        case 'requires_action':
        case 'requires_confirmation':
            return 'stalled';
        // Cancelled or refused at the provider: nothing was taken, another method can pay.
        case 'requires_payment_method':
        case 'canceled':
            return 'canceled';
        default:
            return 'wait';
    }
}

/** This tab only, like the buyer's details: a return URL opened in another tab has no business with it. */
export const PENDING_REDIRECT_KEY = 'checkout-pending-redirect';

export function readPendingRedirect(storage: Pick<Storage, 'getItem'> | undefined): PendingRedirect | null {
    try {
        const stored = storage?.getItem(PENDING_REDIRECT_KEY);
        const parsed = stored ? (JSON.parse(stored) as Partial<PendingRedirect>) : null;
        return typeof parsed?.clientSecret === 'string' && typeof parsed.cartUuid === 'string'
            ? { clientSecret: parsed.clientSecret, method: parsed.method ?? null, cartUuid: parsed.cartUuid }
            : null;
    } catch {
        return null;
    }
}

export function writePendingRedirect(
    storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined,
    pending: PendingRedirect | null
): void {
    try {
        if (pending) storage?.setItem(PENDING_REDIRECT_KEY, JSON.stringify(pending));
        else storage?.removeItem(PENDING_REDIRECT_KEY);
    } catch {
        // Blocked storage: the tab just cannot recheck after a reload.
    }
}
